import { createStore } from "solid-js/store";
import { Show, onMount } from "solid-js";
import _ from "underscore";
import { useNavigate, useRouteData } from "@solidjs/router";
import { fetchPost, scrollToTop } from "../library/common";
import { AtelierImageModel, ImageFileDataListItem, ImageType } from "../types";
import { useForm } from "../components/universal/validation";
import { Progress } from "../components/progress";
import { Required } from "../components/universal/required-icon";
import toast from "solid-toast";
import { getArtworksCountJson } from "../app";
import { CMS } from "../components/cms/cms";
import { ModelEditor, ModelImageCropper, ModelProvider, ModelTextBox } from "../components/model-form-elements";
import { Img } from "../components/universal/image";
import { PageLoading } from "../components/page-loading";


type State = {
    isContentReady: boolean;
    title: string;
    progress: number;
    showImageCropper: boolean;
}

type ButtonActiveState = {
    submit: boolean;
}

export function EditAtelierImage() {
    const notify = {
        uploadingImages: (atelierImage: AtelierImageModel, imageFiles: ImageFileDataListItem[]) => {
            const numImages = imageFiles.length == 1 ? "image" : "images";
            return toast.loading(`Attaching your ${numImages} to "${atelierImage.NewImage.Name}" ...`);
        },
        imageUploadError: (toastId: string, imageFile: ImageFileDataListItem) => {
            toast.error("[003] There was an error attaching an image to the application. The image file is: " + imageFile.file.filename, {
                id: toastId
            });
        },
        uploadedImages: (toastId: string, atelierImage: AtelierImageModel,imageFiles: ImageFileDataListItem[]) => {
            const numImages = imageFiles.length == 1 ? "image" : "images";
            const wasWere = imageFiles.length == 1 ? "was" : "were";
            toast.success(`${imageFiles.length} ${numImages} ${wasWere} uploaded to "${atelierImage.NewImage.Name}".`, {
                id: toastId
            });
        },
        savedAtelierImage: () => toast.success(`Saved the ${model.ImageType}.`),
        commentRequired: () => toast.error("You must provide a description."),
        noImageSelected: () => toast.error("No images selected."),
        imageRequired: () => toast.error("[004] Please attach an image."),
        invalidImage: () => toast.error("[001] The image file is invalid; please check that the file format is either JPG or PNG."),
        unableToSaveImages: () => toast.error(`[002] Unable to save the ${model.ImageType} at this time.`)
    };
    const url = {
        save: "/AtelierApplication/SaveAtelierImageJson",
        saveArtwork: "/LivingArtistApplication/SaveArtworkJson",
        saveImage: "/AtelierApplication/SaveImageJson"
    };
    const [state, setState] = createStore<State>({
        isContentReady: false,
        title: "",
        progress: 0,
        showImageCropper: false
    } as State);
    const [buttonActive, setButtonActive] = createStore<ButtonActiveState>({ submit: true } as ButtonActiveState);
    const { formSubmit, validate, errors, setErrors } = useForm({
        errorClass: "error-input"
    });  
    const navigate = useNavigate();
    const [model, setModel] = createStore<AtelierImageModel>({
        Name: "",
        Description: "",
        OriginalFilename: ""        
    } as AtelierImageModel);
    const getModel = async () => {
        const imageFile = await model.NewImageEditor?.getImageFile();
        const description = model.DescriptionEditor?.getHtml();
        const image = imageFile?.image;
        const filename = imageFile?.filename || "";
        const extension = filename.split('.').at(-1);
        return {
            Id: model.Id,
            AtelierId: model.AtelierId,
            Name: model.Name,
            Description: description,
            ImageType: model.ImageType,
            Priority: model.Priority,
            NewImageId: model.NewImage?.Id,
            ImageFile: image,
            OriginalFilename: filename,
            FilenameExtension: extension
        };
    };
    const validateOtherFields = async () => {
        var isValid = true;
        const imageFile = await model.NewImageEditor?.getImageFile();
        if (!imageFile) {
            if (!model.NewImage?.URL) {
                toast("Please ensure that you have attached a photo.");
                isValid = false;
            }
        } else {
            if (!imageFile?.isValid) {
                toast("Please ensure that you have uploaded a photo in the correct image file type (JPG, PNG).");
                isValid = false;
            }
        }
        return isValid;
    };
    const submitIfValid = async () => {
        setButtonActive("submit", false);
        const isValid = await validateOtherFields();
        if (!isValid) {
            return;
        }
        await save();
        await getArtworksCountJson({ forceRefresh: true });
        setButtonActive("submit", true);
    };
    const navigateToPrevious = () => {
        if (model.ImageType == ImageType.InstructorWork) return navigate("/instructor-works");
        if (model.ImageType == ImageType.StudentWork) return navigate("/student-works");
        if (model.ImageType == ImageType.Photos) return navigate("/other-images");
        return navigate("/not-found");
    }
    const cancelClicked = (e: Event) => {
        e.preventDefault();
        navigateToPrevious();
    }
    const save = async () => {
        const model = await getModel();
        const res = await fetchPost("/AtelierApplication/SaveAtelierImageJson", model);
        const json = await res.json();
        if (!json.Success) {
            return notify.unableToSaveImages();
        }
        navigateToPrevious();
    };
    const changeImageClicked = (e: Event) => {
        e.preventDefault();
        setState("showImageCropper", true);
    }
    const updateProgressBar = (type: string) => {
        const _progress = type == ImageType.InstructorWork ? 3 : type == ImageType.StudentWork ? 4 : 5;
        setState("progress", _progress);
    };
    onMount(async () => {
        const routeData = await useRouteData() as any;
        let atelierImage = routeData.atelierImage as AtelierImageModel;
        setModel(atelierImage as AtelierImageModel);
        if (atelierImage.NewImage) {
            setModel("NewImage", atelierImage.NewImage);
        }
        const type = atelierImage.ImageType;          
        updateProgressBar(type);
        const noun = type == ImageType.InstructorWork ? "instructor work" : type == ImageType.StudentWork ? "student work" : "an atelier image";
        const verb = !!atelierImage.Id ? "Edit" : "Add";
        const title = `${verb} ${noun}`;
        setState("title", title);
        setState({ isContentReady: true, showImageCropper: !atelierImage.NewImage?.URL  });
        scrollToTop();
    });
    return (
        <>
            <Show when={state.isContentReady} fallback={<PageLoading />}>
                <ModelProvider model={model} setModel={setModel} validation={{ errors, validate }}>
                    <div>
                        <Progress step={state.progress} />
                        <form use:formSubmit={submitIfValid}>
                            <div class="row">
                                <div class="col-md-6" style="padding-right: 20px;">
                                    <div class="form-group">
                                        <h4 class="m-t-xxl m-b-lg" style="font-family: 'Oswald'; font-size: 3em;">
                                            <CMS id="label::artwork-details">{state.title}</CMS>
                                            <Show when={!!model.Id}>
                                                <small># {model.Id}</small>
                                            </Show>
                                        </h4>
                                        <p>
                                            <CMS id="message::required-fields">
                                                Fields that are marked with a <span class="fa fa-star" style="color: red;"></span> are required.
                                            </CMS>
                                        </p>
                                        <div class="row">
                                            <div class="col-md-12">
                                                <ModelTextBox<AtelierImageModel>
                                                    field="Name"
                                                    label="Caption"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div class="row" style="margin-top: 20px;">
                                            <div class="col-md-12">
                                                <ModelEditor<AtelierImageModel>
                                                    field="Description" editor="DescriptionEditor"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6" style="padding-left: 20px;">
                                    <h4 class="salon-heading-md m-t-xxl m-b-lg">
                                        <CMS id="salon-application::heading/upload-artwork-images" text>Upload image</CMS>
                                    </h4>
                                    <CMS id="scholarship-application/edit-artwork::images/instructions">
                                        <h4>
                                            Image
                                        </h4>
                                        <p>
                                            Image should be in either JPEG or PNG format and should be between 1MB and 10MB in size.
                                        </p>
                                    </CMS>
                                    <div style="border: 1px solid #ccc; padding: 30px; margin: 0px; margin-top: 25px; border-radius: 10px;">
                                        <div>
                                            <label style="font-size: 1.5em;">
                                                <Required /> <CMS id="label::image" text>Image</CMS>
                                            </label>
                                            <Show when={state.showImageCropper}>
                                                <ModelImageCropper<AtelierImageModel>
                                                    imageUrl={() => model.NewImage?.URL}
                                                    editor="NewImageEditor"
                                                    required
                                                />    
                                            </Show>
                                            <Show when={!state.showImageCropper}>
                                                <div>
                                                    <div>
                                                        <Img src={model.NewImage.URL} width={800} style="width: 100%;" />
                                                    </div>
                                                    <div style="display: table-cell; padding: 10px; vertical-align: middle; width: 100%;">
                                                        <button class="btn btn-primary" onClick={changeImageClicked}>Change Image</button>
                                                    </div>
                                                </div>
                                            </Show>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Show when={model.Id == undefined}>
                                <div class="row">
                                    <div class="col-md-6">
                                        <p>
                                            <CMS id="salon-application/edit-artwork::message/please-be-patient">
                                                <strong>Note:</strong> After you press the button to submit it may take quite a while for the image to upload, please be patient!
                                            </CMS>
                                        </p>
                                    </div>
                                </div>
                            </Show>
                            <div class="row m-t-xxl">
                                <div class="col-md-12">
                                    <div id="submit-row">
                                        <button type="button" class="btn btn-default" onClick={cancelClicked}><CMS id="button::cancel">Cancel</CMS></button>
                                        <button type="submit" class="btn btn-primary" classList={{ ["disable"] : !buttonActive.submit }} style="margin-right: 20px;">
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </ModelProvider>
            </Show>
        </>
    );
}